// src/screens/pawn/Pawn.jsx (หรือ path เดิมของคุณ)
import React, { useEffect, useMemo, useState } from "react";
import { Search, PlusCircle } from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

import PageHeader from "../../components/layout/PageHeader";
import DataTable from "../../components/table/Datatable";
import PaginationFooter from "../../components/table/PaginationFooter";
import DateRangeButton from "../../components/inputs/DateRangeButton";
import { ReactComponent as Banknote } from "../../assets/images/Banknote.svg";
import BatteryPill from "../../components/battery/battery";

import { createHttpClient } from "../../api/client"; // ✅ ปรับ path ให้ตรงโปรเจกต์คุณ

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function buildCreatedAtDateParams(range) {
  const [start, end] = Array.isArray(range) ? range : [null, null];
  if (!start || !end) return {};

  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return {};

  s.setHours(0, 0, 0, 0);
  e.setHours(23, 59, 59, 999);

  return {
    "filters[createdAt][$gte]": s.toISOString(),
    "filters[createdAt][$lte]": e.toISOString(),
  };
}

function compareText(a, b) {
  const A = String(a ?? "").toLowerCase();
  const B = String(b ?? "").toLowerCase();
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}

function compareNumber(a, b) {
  const A = Number(a ?? 0);
  const B = Number(b ?? 0);
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}

function PawnStatusPill({ value }) {
  // ✅ map จาก API ที่เห็นจาก response: active, redeemed, inactive, expired
  const map = {
    active: "bg-[#FF95021A] text-[#FF9502]",
    redeemed: "bg-[#30CA171A] text-[#30CA17]",
    inactive: "bg-[#E5E7EB] text-[#111827]",
    expired: "bg-[#E5E7EB] text-[#111827]",
  };
  const tone = map[value] || "bg-slate-100 text-slate-700";

  const label =
    value === "active" ? "จำนำ" :
      value === "redeemed" ? "ไถ่ถอน" :
        value === "inactive" ? "ยกเลิก" :
          value === "expired" ? "หมดอายุ" :
            (value != null && value !== undefined) ? value : "รออัพเดท"; // fallback ถ้าเป็น undefined/null แต่ไม่ใช่ค่าว่าง

  return (
    <span
      className={classNames(
        "inline-flex min-w-[90px] justify-center rounded-xl px-3 py-1 text-[12px] font-bold",
        tone
      )}
    >
      {label}
    </span>
  );
}

// ✅ แปลง row จาก API -> รูปแบบที่ตารางเดิมใช้
function mapPawnRow(x) {
  const amount = Number(x?.amount ?? 0);
  const productName = String(x?.product_name || "").trim();
  const categoryName = String(x?.category?.name || "").trim();
  const modelName = String(x?.model?.name || (typeof x?.model === "string" ? x.model : "") || "").trim();
  const capacityText = x?.capacity?.size || x?.capacity?.name || x?.capacity || "-";
  const colorText = x?.color?.name || x?.color || "-";
  const equipmentText = Array.isArray(x?.equipments)
    ? x.equipments
        .map((it) => String(it?.name || "").trim())
        .filter(Boolean)
        .join(", ")
    : x?.equipment || "-";
  const itemName =
    productName ||
    x?.item_name ||
    (categoryName && modelName ? `${categoryName} ${modelName}` : categoryName || modelName) ||
    "-";
  const warrantyText =
    x?.warranty_info ||
    x?.warranty_status ||
    (x?.warranty_end_date ? `ประกันศูนย์ถึง ${x.warranty_end_date}` : x?.warranty_date || "-");

  return {
    id: x?.id ?? x?.documentId,
    pawnNo: x?.id ?? "-", // เลขฝาก
    item: itemName, // รายการ (เลือก field ที่มีจริง)
    cap: capacityText,
    color: colorText,
    battery: x?.battery_health ? Number(String(x.battery_health).replace("%", "")) : null,
    gear: equipmentText,
    receivePrice: amount, // ราคารับ
    itemOwner: x?.customer_name || "-",
    phone: x?.customer_phone || "-",
    warranty: warrantyText,
    status: x?.pawn_status || x?.status || "-", // ✅ ใช้ pawn_status ก่อน ถ้ามี ถึงใช้ status แทน
    // เผื่อใช้ต่อ
    __raw: x,
  };
}

export default function PawnListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const http = useMemo(() => createHttpClient(), []);

  const [query, setQuery] = useState("");
  const [range, setRange] = useState([null, null]);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // ✅ sort state
  const [sortKey, setSortKey] = useState(""); // "pawnNo" | "item" | "receivePrice"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  // ✅ data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const onSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
  };

  const fetchPawns = async (signal) => {
    setLoading(true);
    setLoadErr("");
    try {
      const params = {
        ...buildCreatedAtDateParams(range),
      };
      const res = await http.get("/pawns", { params, signal });
      if (signal?.aborted) return;
      const list = res?.data?.data || [];
      const mapped = list.map(mapPawnRow);
      setRows(mapped);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.name === "AbortError") return;
      if (signal?.aborted) return;
      console.error("GET /pawns error:", e);
      setLoadErr("โหลดรายการจำนำไม่สำเร็จ");
      setRows([]);
    } finally {
      if (signal?.aborted) return;
      setLoading(false);
    }
  };

  // ✅ fetch /pawns
  useEffect(() => {
    const ac = new AbortController();
    fetchPawns(ac.signal);
    return () => {
      ac.abort();
    };
  }, [http, range?.[0], range?.[1]]);

  // ✅ Refetch หลังจากหน้าอื่นเมื่อกลับมา (เช่น หลังจากหน้าไถ่ถอน)
  useEffect(() => {
    if (!location.state?.refetch) return;
    if (location.state?.refetch === true) {
      // ล้างค่า state ทิ้ง
      window.history.replaceState({}, document.title);
      // รีเฟตช์ข้อมูล
      const ac = new AbortController();
      fetchPawns(ac.signal);
      return () => {
        ac.abort();
      };
    }
  }, [location.state?.refetch, http]);

  const filtered = useMemo(() => {
    let next = [...rows];
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      next = next.filter((r) =>
        [
          r.pawnNo,
          r.item,
          r.cap,
          r.color,
          r.gear,
          r.receivePrice,
          r.phone,
          r.warranty,
          r.status,
          r.itemOwner,
        ]
          .map((x) => String(x ?? "").toLowerCase())
          .some((x) => x.includes(q))
      );
    }
    return next;
  }, [rows, query]);

  // ✅ sort หลัง filter (ก่อน paginate)
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;

    const next = [...filtered].sort((a, b) => {
      if (sortKey === "pawnNo") {
        // ticket_number เป็น string บางทีเรียงแบบ text จะดีกว่า
        const c = compareText(a?.pawnNo, b?.pawnNo);
        return sortDir === "asc" ? c : -c;
      }
      if (sortKey === "receivePrice") {
        const c = compareNumber(a?.receivePrice, b?.receivePrice);
        return sortDir === "asc" ? c : -c;
      }
      // item -> text
      const c = compareText(a?.item, b?.item);
      return sortDir === "asc" ? c : -c;
    });

    return next;
  }, [filtered, sortKey, sortDir]);

  const totalItems = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  const columns = useMemo(
    () => [
      {
        key: "pawnNo",
        title: "เลขฝาก",
        className: "font-semibold",
        sortable: true,
        sortKey: "pawnNo",
        render: (r) => r.id,
      },
      {
        key: "item",
        title: "รายการ",
        className: "font-semibold",
        headerClassName: "w-[350px]",
        sortable: true,
        sortKey: "item",
      },
      { key: "cap", title: "ความจุ" },
      { key: "color", title: "สี" },
      {
        key: "battery",
        title: "แบตเตอรี่",
        render: (r) => (r.battery != null ? <BatteryPill value={r.battery} /> : "-"),
      },
      { key: "gear", title: "อุปกรณ์" },
      {
        key: "receivePrice",
        title: "ราคารับ",
        sortable: true,
        sortKey: "receivePrice",
        render: (r) => `฿${Number(r.receivePrice || 0).toLocaleString("th-TH")}`,
      },
      {
        key: "itemOwner",
        title: "ชื่อผู้ฝาก",
        sortable: false,
        sortKey: "itemOwner",
        render: (r) => r.itemOwner,
      },
      { key: "phone", title: "เบอร์โทรศัพท์" },
      { key: "warranty", title: "ประกันศูนย์" },
      {
        key: "status",
        title: "สถานะ",
        render: (r) => <PawnStatusPill value={r.status} />,
      },
      {
        key: "manage",
        title: "จัดการ",
        render: (r) => (
          <NavLink
            to={`/app/pawn/create/${encodeURIComponent(r.id)}`}
            className="inline-flex items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5] hover:border-none"
          >
            จัดการ
          </NavLink>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-4 p-4 bg-white min-h-full">
      <PageHeader
        breadcrumb="จำนำ"
        icon={Banknote}
        title="รายการจำนำ"
        showExport={false}
        extraActions={
          <button
            type="button"
            onClick={() => navigate("/app/pawn/create")}
            className="inline-flex items-center gap-2 rounded-xl bg-[#111111] px-4 py-3 text-sm font-bold text-white shadow-md hover:opacity-95 active:scale-[0.99]"
          >
            <PlusCircle size={18} />
            เพิ่มรายการจำนำ
          </button>
        }
      />

      {/* search bar */}
      <div className="rounded-2xl bg-[#F5F5F5] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="w-full md:max-w-[520px] space-y-2">
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <Search size={18} className="text-[#1D1616B2]" />
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680]"
                placeholder="กรอกเลขฝากจำนำ"
              />
            </div>

            {loading && <div className="text-xs text-slate-500">กำลังโหลดข้อมูล...</div>}
            {loadErr && <div className="text-xs text-rose-600">{loadErr}</div>}
          </div>

          <div className="flex w-full justify-start md:w-auto md:justify-end">
            <DateRangeButton mode="single" range={range} setRange={setRange} />
          </div>
        </div>
      </div>

      {/* table */}
      <div className="pl-0">
        <DataTable
          columns={columns}
          data={paged}
          rowHeight="h-16"
          headerBg="bg-slate-50"
          striped={false}
          tableMinWidth="min-w-[1100px]"
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
        />
      </div>

      <PaginationFooter
        totalItems={totalItems}
        page={safePage}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        pageSizeOptions={[50, 100, 200]}
      />
    </div>
  );
}

